import { CircleAlert } from 'lucide-react';
import {
  useId,
  useMemo,
  type HTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from './cn';
import { FieldContext, type FieldContextValue } from './fieldContext';

export type LabelProps = LabelHTMLAttributes<HTMLLabelElement>;

/** Form label: text-xs, medium weight, muted. */
export const Label = ({ className, ...props }: LabelProps) => (
  <label
    className={cn('text-xs font-medium text-fg-muted', className)}
    {...props}
  />
);

/** Supporting text under a control. Give it an id and reference it. */
export const HelpText = ({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cn('text-xs leading-relaxed text-fg-muted', className)}
    {...props}
  />
);

/** Validation message under a control. */
export const FieldError = ({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cn(
      'flex items-start gap-1 text-xs font-medium text-danger-fg',
      className
    )}
    {...props}
  >
    <CircleAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
    <span>{children}</span>
  </p>
);

export interface FieldProps {
  label: ReactNode;
  /** One control: `Input`, `Textarea`, or `Select` (they read the wiring). */
  children: ReactNode;
  help?: ReactNode;
  /** Validation message; marks the control `aria-invalid`. */
  error?: ReactNode;
  /** id for the control. Set it here, not on the control. */
  id?: string;
  required?: boolean;
  disabled?: boolean;
  /** Extra element aligned with the label, such as an `InfoTooltip`. */
  labelAccessory?: ReactNode;
  className?: string;
}

/**
 * Label + control + help/error text with `htmlFor`, `aria-describedby`,
 * `aria-invalid`, and `required` wired up.
 */
export const Field = ({
  label,
  children,
  help,
  error,
  id,
  required = false,
  disabled = false,
  labelAccessory,
  className,
}: FieldProps) => {
  const autoId = useId();
  const controlId = id ?? `field-${autoId}`;
  const helpId = `${controlId}-help`;
  const errorId = `${controlId}-error`;
  const hasHelp = help !== undefined && help !== null && help !== '';
  const hasError =
    error !== undefined && error !== null && error !== '' && error !== false;

  const context = useMemo<FieldContextValue>(
    () => ({
      controlId,
      describedBy:
        [hasHelp ? helpId : undefined, hasError ? errorId : undefined]
          .filter(Boolean)
          .join(' ') || undefined,
      invalid: hasError,
      required,
      disabled,
    }),
    [controlId, hasHelp, helpId, hasError, errorId, required, disabled]
  );

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex min-h-4 items-center justify-between gap-2">
        <Label htmlFor={controlId}>
          {label}
          {required && (
            <span aria-hidden="true" className="ml-0.5 text-fg-muted">
              *
            </span>
          )}
        </Label>
        {labelAccessory}
      </div>
      <FieldContext.Provider value={context}>{children}</FieldContext.Provider>
      {hasHelp && <HelpText id={helpId}>{help}</HelpText>}
      {hasError && <FieldError id={errorId}>{error}</FieldError>}
    </div>
  );
};
